import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/lib/api'

/**
 * Single upserted row (spec Requirement 5). State is a manual on/off toggle;
 * starts_at/ends_at layer an optional window on top — the consumer site shows
 * the banner only when state="live" AND now falls within the window (either
 * bound null means unbounded on that side).
 *
 * The schema is the executable copy of the `PUT /promo-banner` body in
 * docs/api-contract.md; validation and the request shape stay in step because
 * they are the same definition.
 */
export const bannerSchema = z
  .object({
    image_url: z.string().min(1, 'An image is required.'),
    /**
     * Where the banner sends a member who taps it. Either an absolute URL or a
     * site-relative path starting with "/" — anything else is almost always a
     * typo that would 404 on the consumer app, and a banner nobody can click
     * through is worse than no banner.
     */
    link_url: z
      .string()
      .min(1, 'A destination is required.')
      .refine(
        (value) => value.startsWith('/') || /^https?:\/\//.test(value),
        'Use an absolute URL (https://…) or a site-relative path starting with "/".',
      ),
    state: z.enum(['live', 'off']),
    // ISO-8601 UTC on the wire. null is meaningful on both: no scheduled start
    // (live as soon as state=live) and no expiry.
    starts_at: z.string().nullable(),
    ends_at: z.string().nullable(),
  })
  .superRefine((banner, ctx) => {
    // A window that ends before it starts can never be live, so the banner
    // would silently never show after saving.
    if (banner.starts_at && banner.ends_at && banner.ends_at <= banner.starts_at) {
      ctx.addIssue({ code: 'custom', path: ['ends_at'], message: 'The end must come after the start.' })
    }
  })

export type BannerInput = z.infer<typeof bannerSchema>

export interface Banner extends BannerInput {
  id: number
  updated_by: string | null
  updated_at: string | null
}

const KEY = ['promo-banner']

export function usePromoBanner() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.get<Banner>('/admin/promo-banner'),
  })
}

export function useSavePromoBanner() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: BannerInput) => api.put<Banner>('/admin/promo-banner', input),
    onSuccess: (saved) => queryClient.setQueryData(KEY, saved),
  })
}
